import {Radio} from '@ui/forms/radio-group/radio';
import {UpsellBillingCycle} from './find-best-price';
import {Trans} from '@ui/i18n/trans';
import {RadioGroup, RadioGroupProps} from '@ui/forms/radio-group/radio-group';
import {Product} from '../product';
import {useId} from 'react';

interface BillingCycleRadioProps extends Omit<RadioGroupProps, 'children'> {
  selectedCycle: UpsellBillingCycle;
  onChange: (value: UpsellBillingCycle) => void;
  products?: Product[];
}
export function BillingCycleRadio({
  selectedCycle,
  onChange,
  products,
  className,
  ...radioGroupProps
}: BillingCycleRadioProps) {
  const mobileName = useId();
  const paidPlan =
    products?.find(plan => !plan.free && !plan.hidden && plan.recommended) ??
    products?.find(plan => !plan.free && !plan.hidden);

  return (
    <div className={className}>
      <RadioGroup {...radioGroupProps} className="hidden md:block">
        {cycles.map(({value, label}) => (
          <Radio
            key={value}
            value={value}
            checked={selectedCycle === value}
            onChange={() => onChange(value)}
          >
            <Trans message={label} />
          </Radio>
        ))}
      </RadioGroup>

      <fieldset className="w-full max-w-440 py-4 md:hidden">
        <legend className="sr-only">
          <Trans message="Billing period" />
        </legend>
        <div className="flex w-full flex-nowrap items-center justify-between gap-4">
          {cycles.map(({value, label}) => {
            const price = paidPlan ? findCyclePrice(value, paidPlan) : undefined;
            const isDisabled = radioGroupProps.disabled || (!!paidPlan && !price);

            return (
              <Radio
              key={value}
                size="xs"
                name={mobileName}
                value={value}
                checked={selectedCycle === value}
                disabled={isDisabled}
                required={radioGroupProps.required}
                onChange={() => onChange(value)}
              >
                <Trans message={label} />
              </Radio>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}

const cycles: {value: UpsellBillingCycle; label: string}[] = [
  {value: 'yearly', label: 'Annual'},
  {value: 'semiannual', label: 'Semiannual'},
  {value: 'quarterly', label: 'Quarterly'},
  {value: 'monthly', label: 'Monthly'},
];

function findCyclePrice(cycle: UpsellBillingCycle, product: Product) {
  return product.prices.find(price => {
    if (cycle === 'monthly') {
      return (
        (price.interval === 'month' && price.interval_count === 1) ||
        (price.interval === 'day' && price.interval_count >= 30)
      );
    }
    if (cycle === 'quarterly') {
      return price.interval === 'month' && price.interval_count === 3;
    }
    if (cycle === 'semiannual') {
      return price.interval === 'month' && price.interval_count === 6;
    }
    return (
      (price.interval === 'month' && price.interval_count >= 12) ||
      (price.interval === 'year' && price.interval_count >= 1)
    );
  });
}
